/*
 * A synthetic Smart Health Report for the demo patient "Rohan Mehta, M, 34".
 * Every value is made up. Used by the report mocks on several product screens
 * (and by the AI Video screen, now out of the flow): wellness score 72 (Good),
 * with 4 results to watch.
 */

export const SAMPLE_PATIENT = {
  name: 'Rohan Mehta',
  short: 'Rohan Mehta, M, 34',
  details: 'Male | 34 years',
  testDate: '12 Oct 2026',
  id: 'NG-DEMO-0034',
};

export const SAMPLE_SCORE = { value: 72, label: 'Good', toWatch: 4 };

/* Status words are always shown next to their colour. */
export const STATUS_LABELS = {
  normal: 'Normal',
  borderline: 'Borderline',
  high: 'High',
  low: 'Low',
};

/* Badge tone for each status (`.badge--<tone>`); Low shares the red tone. */
export const STATUS_TONE = {
  normal: 'normal',
  borderline: 'borderline',
  high: 'high',
  low: 'high',
};

export const MARKERS = {
  hb: {
    name: 'Haemoglobin',
    value: '14.2',
    unit: 'g/dL',
    range: '13.0 – 17.0',
    status: 'normal',
    note: 'Carries oxygen around your body.',
  },
  rbc: {
    name: 'Total RBC count',
    value: '4.9',
    unit: 'million/µL',
    range: '4.5 – 5.5',
    status: 'normal',
    note: 'The number of red blood cells.',
  },
  hct: {
    name: 'Haematocrit (HCT)',
    value: '43',
    unit: '%',
    range: '40 – 50',
    status: 'normal',
    note: 'How much of your blood is red cells.',
  },
  mcv: {
    name: 'Mean corpuscular volume (MCV)',
    value: '88',
    unit: 'fL',
    range: '83 – 101',
    status: 'normal',
    note: 'The average size of your red cells.',
  },
  wbc: {
    name: 'Total WBC count',
    value: '7,200',
    unit: '/µL',
    range: '4,000 – 10,000',
    status: 'normal',
    note: 'White cells that fight infection.',
  },
  plt: {
    name: 'Platelet count',
    value: '2.4',
    unit: 'lakh/µL',
    range: '1.5 – 4.1',
    status: 'normal',
    note: 'Cells that help your blood clot.',
  },
  tc: {
    name: 'Total cholesterol',
    value: '234',
    unit: 'mg/dL',
    range: 'Below 200',
    status: 'high',
    note: 'All the cholesterol in your blood.',
  },
  ldl: {
    name: 'LDL cholesterol',
    value: '162',
    unit: 'mg/dL',
    range: 'Below 100',
    status: 'high',
    note: 'The cholesterol that can build up in blood vessels.',
  },
  hdl: {
    name: 'HDL cholesterol',
    value: '44',
    unit: 'mg/dL',
    range: 'Above 40',
    status: 'normal',
    note: 'The “good” cholesterol that clears the rest.',
  },
  tg: {
    name: 'Triglycerides',
    value: '138',
    unit: 'mg/dL',
    range: 'Below 150',
    status: 'normal',
    note: 'Fat from food, stored for energy.',
  },
  hba1c: {
    name: 'HbA1c',
    value: '6.4',
    unit: '%',
    range: 'Below 5.7',
    status: 'borderline',
    note: 'Your average blood sugar over three months.',
  },
  tsh: {
    name: 'TSH',
    value: '2.4',
    unit: 'mIU/L',
    range: '0.4 – 4.0',
    status: 'normal',
    note: 'Shows how well your thyroid is working.',
  },
  vitd: {
    name: 'Vitamin D',
    value: '18',
    unit: 'ng/mL',
    range: '30 – 100',
    status: 'low',
    note: 'Keeps bones and muscles strong.',
  },
};
